import type { ComponentProps } from "react";
import { Separator as SeparatorPrimitive } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { Heading } from "@/shared/ui/common/heading";

const styles = tv({
  slots: {
    root: "relative flex flex-col gap-3 rounded-xl border border-secondary-100 bg-secondary-50 p-4",
    header: "flex gap-3",
    description: "text-sm font-medium",
    footer: "flex items-center",
    separator: "h-px w-full bg-secondary-200 last:hidden",
  },
  variants: {
    ghost: {
      true: {
        root: "border-transparent bg-transparent p-0",
      },
    },
  },
});

const { root, header, description, footer, separator } = styles();

const Root = ({
  className,
  ghost = false,
  ...props
}: ComponentProps<"div"> & VariantProps<typeof styles>) => (
  <div className={root({ className, ghost })} {...props} />
);

const Header = ({ className, ...props }: ComponentProps<"div">) => (
  <div className={header({ className })} {...props} />
);

const Title = ({
  level = 2,
  ...props
}: Omit<ComponentProps<typeof Heading>, "size">) => (
  <Heading level={level} size="xx-small" data-slot="card-title" {...props} />
);

const Description = ({ className, ...props }: ComponentProps<"p">) => (
  <p className={description({ className })} {...props} />
);

const Content = (props: ComponentProps<"div">) => <div {...props} />;

const Footer = ({ className, ...props }: ComponentProps<"div">) => (
  <div className={footer({ className })} {...props} />
);

const Separator = ({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}: ComponentProps<typeof SeparatorPrimitive.Root>) => (
  <SeparatorPrimitive.Root
    asChild
    decorative={decorative}
    orientation={orientation}
    className={separator({ className })}
    {...props}
  >
    <span />
  </SeparatorPrimitive.Root>
);

export const Card = {
  Root,
  Header,
  Footer,
  Title,
  Description,
  Content,
  Separator,
};
